import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useVisitor } from "@/context/VisitorContext";
import { EASE } from "@/components/ui/Reveal";

export default function GreetingModal() {
  const { name, promptOpen, setPromptOpen, introduce, forget } = useVisitor();
  const [draft, setDraft] = useState("");
  const [greeted, setGreeted] = useState(false);
  const inputRef = useRef(null);

  const nameRef = useRef(name);
  nameRef.current = name;

  useEffect(() => {
    if (!promptOpen) return undefined;
    setGreeted(false);
    setDraft(nameRef.current || "");
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 350);
    return () => clearTimeout(t);
  }, [promptOpen]);

  useEffect(() => {
    if (!promptOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPromptOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [promptOpen, setPromptOpen]);

  const submit = (e) => {
    if (e) e.preventDefault();
    if (introduce(draft)) {
      setGreeted(true);
      toast(`Welcome, ${draft.trim()} — make yourself at home.`);
      setTimeout(() => setPromptOpen(false), 1700);
    }
  };

  const skip = () => setPromptOpen(false);

  return (
    <AnimatePresence>
      {promptOpen && (
        <motion.div
          className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={skip}
          data-testid="greeting-modal"
        >
          <motion.div
            className="glass w-full max-w-md overflow-hidden rounded-xl"
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 0.35, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-8 items-center border-b border-white/10 bg-white/[.05] px-3">
              <div className="flex items-center gap-1.5">
                <button onClick={skip} aria-label="Close greeting" data-testid="greeting-close" className="h-2.5 w-2.5 rounded-full bg-[#FF5F56] transition-transform duration-150 hover:scale-110" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FFBD2E]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#27C93F]" />
              </div>
              <p className="flex-1 text-center font-mono text-[10px] text-slate-500">visitor-intro — zsh</p>
            </div>
            <div className="space-y-3 p-5 font-mono text-[13px]" data-testid="greeting-card">
              <p>
                <span className="text-green-400">➜</span> <span className="text-sky-300">~</span>{" "}
                <span className="text-slate-200">who&apos;s visiting today?</span>
              </p>
              {greeted ? (
                <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-green-300" data-testid="greeting-message">
                  Hello, {name}. Welcome to my workspace — make yourself at home.
                </motion.p>
              ) : (
                <form onSubmit={submit} className="flex items-center gap-2" data-testid="greeting-form">
                  <span className="text-slate-500">$</span>
                  <input
                    ref={inputRef}
                    data-testid="greeting-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    maxLength={40}
                    placeholder="type your name…"
                    aria-label="Your name"
                    className="flex-1 bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600"
                  />
                  <button type="submit" data-testid="greeting-submit" className="rounded-md bg-sonoma px-3 py-1 text-xs text-white transition-colors duration-150 hover:bg-[#0062CC]">
                    enter
                  </button>
                </form>
              )}
              {!greeted && (
                <div className="flex items-center gap-3">
                  <button onClick={skip} data-testid="greeting-skip" className="text-[11px] text-slate-500 transition-colors duration-150 hover:text-slate-300">
                    skip — browse as guest
                  </button>
                  {name ? (
                    <button onClick={forget} data-testid="greeting-clear" className="text-[11px] text-slate-500 transition-colors duration-150 hover:text-slate-300">
                      not {name}? — clear
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
